import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/bporh_admissoes.css';

// Fechamento de Ponto: para cada empresa, o relatório de banco de horas do mês
// (quem tem horas sobrando/devendo) computado das batidas do painel de Ponto.
// Agrupa por departamento (Convenia) e envia ao gestor de cada departamento,
// com os dias sem registro e uma análise gerada por IA (Gemini).

const CRITICO_SEG = 15 * 3600;   // saldo acima de 15h (± ) = crítico
const TOLERANCIA_SEG = 10 * 60;  // CLT art. 58 §1º: até 10min/dia não entra no banco
const SEM_DEP = 'Sem departamento';

// segundos -> "27h27min" (formato do relatório)
const fmtHoras = (seg) => {
  const s = Math.abs(Math.round(seg));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h${String(m).padStart(2, '0')}min`;
};
const fmtSaldo = (seg) => `${seg >= 0 ? '+' : '−'}${fmtHoras(seg)}`;
const h2s = (h) => { const p = (h || '').split(':'); return (+p[0]) * 3600 + (+p[1]) * 60; };
// rótulo do dia da semana (como vem do backend) -> índice 0=seg ... 6=dom
const DIA_IDX = { Seg: 0, Ter: 1, Qua: 2, Qui: 3, Sex: 4, 'Sáb': 5, Dom: 6 };
const folgaFixa = (jm, semana) => Array.isArray(jm?.dias_folga) && jm.dias_folga.includes(DIA_IDX[semana]);

// Quantas batidas o dia trabalhado exige, pelo VÍNCULO do colaborador.
// CLT de jornada cheia precisa marcar o intervalo (4 batidas); aprendiz e
// estagiário costumam fazer até 6h, que não exige intervalo (art. 71 da CLT),
// então 2 batidas fecham o dia.
const BATIDAS_MINIMAS_PADRAO = 4;

// Intervalo intrajornada (CLT art. 71): quem trabalha mais de 6h no dia precisa
// de no mínimo 1h de intervalo. Vale o MAIOR intervalo entre os pares de batida
// (com 6 batidas, a saída rápida de 15 min não é o almoço). Devolve o intervalo
// em segundos quando ficou abaixo de 1h; null quando está ok ou não se aplica.
const INTERVALO_MIN_SEG = 3600;
const JORNADA_EXIGE_INTERVALO_SEG = 6 * 3600;
const intervaloCurto = (b) => {
  if (!b || b.length < 4 || b.length % 2 === 1) return null;
  let worked = 0, maior = 0;
  for (let i = 0; i + 1 < b.length; i += 2) worked += h2s(b[i + 1]) - h2s(b[i]);
  for (let i = 1; i + 1 < b.length; i += 2) maior = Math.max(maior, h2s(b[i + 1]) - h2s(b[i]));
  if (worked <= JORNADA_EXIGE_INTERVALO_SEG) return null;
  return maior < INTERVALO_MIN_SEG ? maior : null;
};
const fmtMin = (seg) => `${Math.round(seg / 60)}min`;

const batidasMinimas = (vinculo) => {
  const v = (vinculo || '').toLowerCase();
  if (v.includes('aprendiz') || v.includes('estag') || v.includes('estág')) return 2;
  return BATIDAS_MINIMAS_PADRAO;
};

const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// cores da etiqueta de situação no relatório
const SITUACAO_FMT = {
  credor: { rotulo: 'Credor', bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971' },
  devedor: { rotulo: 'Devedor', bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545' },
  ferias: { rotulo: 'Férias', bg: '#e3f2fd', cor: '#0a6ea1', borda: '#0a6ea1' },
  afastamento: { rotulo: 'Afastamento', bg: '#f3e8fb', cor: '#7a3b9e', borda: '#7a3b9e' },
  sem_batidas: { rotulo: 'Sem batidas', bg: '#fff8e6', cor: '#8a6d00', borda: '#f1be48' },
  nao_registra: { rotulo: 'Não bate ponto', bg: '#f1f3f5', cor: '#6c757d', borda: '#ced4da' },
};

// nome do serviço cadastrado -> sistema de ponto (mesma regra do backend)
const SISTEMAS_PONTO = [
  ['ponto mais', 'pontomais', 'Ponto Mais'], ['tiquetaque', 'tiquetaque', 'TiqueTaque'],
  ['kairos', 'kairos', 'Kairos'], ['aghora', 'aghora', 'Ponto Aghora'], ['ahgora', 'aghora', 'Ponto Aghora'],
  ['rhid', 'rhid', 'RHiD'],
];
const sistemaDoServico = (propriedade) => {
  const p = (propriedade || '').toLowerCase();
  const hit = SISTEMAS_PONTO.find(([chave]) => p.includes(chave));
  return hit ? { tipo: hit[1], rotulo: hit[2] } : null;
};

const FechamentoPonto = () => {
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
  const API = process.env.REACT_APP_API_BASE;

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // ─── clientes com ponto ───
  const [clientes, setClientes] = useState([]);
  // ?cliente= e ?mes= abrem a tela direto na empresa certa (é assim que a
  // Folha de Pagamento aponta para cá, na rubrica que vem do ponto)
  const [searchParams] = useSearchParams();
  const [clienteId, setClienteId] = useState(searchParams.get('cliente') || '');
  // Cliente com MAIS DE UM sistema de ponto (Gauss: TiqueTaque + Kairos) vira
  // uma entrada por sistema no select; 'sistema' vai em toda chamada ao backend
  const [sistema, setSistema] = useState('');
  // guarda contra corrida: resposta em voo de um cliente antigo não pode
  // pintar a tela do cliente atual (isolamento entre clientes)
  const clienteIdRef = useRef(clienteId);
  useEffect(() => { clienteIdRef.current = clienteId; }, [clienteId]);
  useEffect(() => {
    axios.get(`${API}/api/bporh-servicos/`, { params: { categoria: 'Ponto' }, headers: headers() })
      .then((res) => {
        const vistos = new Map();
        (res.data.servicos || []).forEach(s => {
          if (!s.cliente_id) return;
          if (!vistos.has(s.cliente_id)) vistos.set(s.cliente_id, { id: s.cliente_id, nome: s.cliente, sistemas: [] });
          const sis = sistemaDoServico(s.propriedade);
          const c = vistos.get(s.cliente_id);
          if (sis && !c.sistemas.some(x => x.tipo === sis.tipo)) c.sistemas.push(sis);
        });
        setClientes([...vistos.values()]
          .sort((a, b) => (a.nome || '').localeCompare(b.nome || '')));
      })
      .catch(() => setMensagem('Não foi possível carregar os clientes'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── mês + dados do ponto ───
  const [mes, setMes] = useState(searchParams.get('mes') || mesAtual());
  const [dadosMes, setDadosMes] = useState(null);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(() => {
    if (!clienteId) { setDadosMes(null); return; }
    const cid = clienteId;
    setCarregando(true);
    axios.get(`${API}/api/bpo-ponto/${clienteId}/mes/`, { params: { mes, sistema: sistema || undefined }, headers: headers() })
      .then((res) => { if (clienteIdRef.current === cid) setDadosMes(res.data); })
      .catch((error) => {
        if (clienteIdRef.current !== cid) return;
        setDadosMes(null);
        setMensagem(`${error.response?.data?.erro || 'Não foi possível carregar o ponto'}`);
      })
      .finally(() => { if (clienteIdRef.current === cid) setCarregando(false); });
  }, [clienteId, mes, sistema]); // eslint-disable-line react-hooks/exhaustive-deps
  const carregarRef = useRef(carregar);
  useEffect(() => { carregarRef.current = carregar; }, [carregar]);

  // troca de MÊS: recarrega direto (o espelho já foi atualizado na seleção da empresa)
  useEffect(() => {
    if (clienteId) carregarRef.current();
  }, [mes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── troca de EMPRESA: nada é exibido até atualizar tudo ───
  // Primeiro reimporta o espelho de colaboradores do provedor (a parte que
  // envelhece: quem entrou/saiu, nome, PIS); só então carrega o mês — que por
  // sua vez busca as batidas ao vivo na API. Se o sync falhar (provedor fora),
  // carrega mesmo assim com o espelho existente.
  const [sincronizando, setSincronizando] = useState(false);
  useEffect(() => {
    if (!clienteId) { setDadosMes(null); return undefined; }
    let ativo = true;
    setDadosMes(null);
    setSincronizando(true);
    axios.post(`${API}/api/bpo-ponto/${clienteId}/sincronizar/`, { auto: true, sistema: sistema || undefined }, { headers: headers() })
      .catch(() => {})
      .finally(() => {
        if (!ativo) return;
        setSincronizando(false);
        carregarRef.current();
      });
    return () => { ativo = false; };
  }, [clienteId, sistema]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── gestores por departamento + operadores ───
  const [gestores, setGestores] = useState([]);   // [{departamento, gestor_nome, gestor_email}]
  const [operadores, setOperadores] = useState([]);
  const [operadorId, setOperadorId] = useState('');
  const [enviando, setEnviando] = useState(false);

  // gestores da EMPRESA (supervisores da Convenia + já cadastrados) p/ o select
  const [gestoresEmpresa, setGestoresEmpresa] = useState([]);

  const carregarGestores = useCallback(() => {
    if (!clienteId) { setGestores([]); setGestoresEmpresa([]); return; }
    const cid = clienteId;
    axios.get(`${API}/api/bpo-ponto/${clienteId}/fechamento/gestor/`, { headers: headers() })
      .then((res) => {
        if (clienteIdRef.current !== cid) return;   // trocou de empresa no meio
        setGestores(res.data.gestores || []);
        setGestoresEmpresa(res.data.gestores_empresa || []);
        setOperadores(res.data.operadores || []);
        const p = (res.data.operadores || []).find(o => o.pronto);
        setOperadorId(pv => pv || (p ? String(p.id) : ''));
      })
      .catch(() => {});
  }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarGestores(); }, [carregarGestores]);

  // ─── alterações de ponto (lazy: endpoint próprio p/ não pesar o /mes) ───
  //   { por_id: {colaborador_id: n}, rotulo, disponivel }  disponivel null = carregando
  const [altInfo, setAltInfo] = useState({ por_id: {}, rotulo: 'Alterações', disponivel: null });
  useEffect(() => {
    if (!clienteId || !dadosMes) { setAltInfo({ por_id: {}, rotulo: 'Alterações', disponivel: null }); return; }
    const cid = clienteId;
    setAltInfo({ por_id: {}, rotulo: 'Alterações', disponivel: null });
    axios.get(`${API}/api/bpo-ponto/${clienteId}/alteracoes/`, { params: { mes, sistema: sistema || undefined }, headers: headers() })
      .then((res) => clienteIdRef.current === cid && setAltInfo({
        por_id: res.data.por_id || {},
        rotulo: res.data.rotulo || 'Alterações',
        disponivel: res.data.disponivel !== false,
      }))
      .catch(() => clienteIdRef.current === cid
        && setAltInfo({ por_id: {}, rotulo: 'Alterações', disponivel: false }));
  }, [clienteId, mes, dadosMes]); // eslint-disable-line react-hooks/exhaustive-deps
  const altDe = (id) => altInfo.por_id[String(id)] || 0;

  // ─── filtro de departamento + gestor do departamento selecionado ───
  const [depFiltro, setDepFiltro] = useState('');   // '' = todos
  const [gestorNome, setGestorNome] = useState('');
  const [gestorEmail, setGestorEmail] = useState('');

  // 'outro' no select libera a digitação livre p/ gestor fora da Convenia
  const [gestorManual, setGestorManual] = useState(false);

  // ao trocar de departamento, carrega o gestor já cadastrado dele
  useEffect(() => {
    const g = gestores.find(x => x.departamento === depFiltro);
    setGestorNome(g?.gestor_nome || '');
    setGestorEmail(g?.gestor_email || '');
    setGestorManual(false);
    setResumo('');   // a análise é por departamento
  }, [depFiltro, gestores]);

  // select de gestor: o valor é DERIVADO do nome (casa com a lista da empresa)
  const gestorSel = gestorManual ? 'outro'
    : (() => {
      const i = gestoresEmpresa.findIndex(
        g => (g.nome || '').trim().toLowerCase() === gestorNome.trim().toLowerCase());
      if (i >= 0) return String(i);
      return gestorNome.trim() ? 'outro' : '';
    })();
  const escolherGestor = (e) => {
    const v = e.target.value;
    if (v === 'outro') { setGestorManual(true); return; }
    setGestorManual(false);
    if (v === '') { setGestorNome(''); setGestorEmail(''); return; }
    const g = gestoresEmpresa[Number(v)];
    if (g) { setGestorNome(g.nome || ''); setGestorEmail(g.email || ''); }
  };

  // ─── cálculo do banco de horas por colaborador (worked - esperado nos dias
  //     úteis com batida). Só considera dias com batida; dias sem registro ficam
  //     de fora do saldo mas são listados como pendência. ───
  const relatorio = useMemo(() => {
    if (!dadosMes) return [];
    const esperadoPadrao = dadosMes.esperado_seg || 0;
    const dias = dadosMes.dias || [];
  const vinculos = dadosMes.vinculos || {};
  const abonos = dadosMes.abonos || {};
    const mm = (dadosMes.mes || '').slice(5);
    const ordem = { credor: 0, devedor: 0, sem_batidas: 1, ferias: 2, afastamento: 3, nao_registra: 4 };
    return (dadosMes.colaboradores || [])
      .map(c => {
        // jornada individual da Convenia; sem cadastro, cai na config da empresa
        const esperado = c.esperado_seg != null ? c.esperado_seg : esperadoPadrao;
        let saldo = 0, extras = 0, devendo = 0, diasOk = 0, temBatida = false;
        const naoBateu = [];   // dias úteis sem registro
        const impares = [];    // dias com nº ímpar de batidas (esqueceu de bater)
        const emAusencia = []; // bateu ponto estando de férias/afastado
        const feriadosTrab = []; // bateu ponto em feriado: horas viram sobra, e o RH precisa saber
        // dias trabalhados com MENOS batidas do que o vínculo exige — CLT com
        // 2 batidas quase sempre é intervalo não marcado
        const incompletos = [];
        const intervalosCurtos = []; // trabalhou mais de 6h com almoço abaixo de 1h
        const vinculo = (c.cpf && vinculos[c.cpf]) || c.vinculo || null;
        const minBatidas = batidasMinimas(vinculo);
        // dias que o operador marcou como folga / não cobrar
        const abonosDele = (c.cpf && abonos[c.cpf]) || {};
        const folgas = [];
        dias.forEach(d => {
          const l = c.dias?.[d.iso];
          if (!l) return;
          // 'fora' = hoje/futuro, antes da admissão ou período não importado;
          // 'isento' = não registra; 'desligado' = depois da saída. Nada desses
          // dias entra no resumo — a batida de HOJE, por exemplo, não pode
          // transformar ninguém em credor/devedor antes de o dia fechar.
          if (l.status === 'fora' || l.status === 'isento' || l.status === 'desligado') return;
          const b = l.batidas || [];
          if (b.length >= 2) temBatida = true;
          const rot = `${String(d.dia).padStart(2, '0')}/${mm}`;
          // dia marcado pelo operador (folga, compensação, dia de campo):
          // não é falta e não espera jornada — sai da conta inteira
          const ab = abonosDele[d.iso];
          if (ab) {
            folgas.push(rot);
            if (b.length === 0) return;
          }
          // feriado não cobra batida: o dia já vem com util=false do backend,
          // e quem não bateu num feriado não é falta
          if (d.feriado) {
            if (b.length === 0) return;
            feriadosTrab.push(`${rot} (${d.feriado})`);
          } else if (!ab && l.status === 'sem_batida') naoBateu.push(rot);
          if (l.status === 'impar') impares.push(rot);
          const ic = intervaloCurto(b);
          if (ic != null) intervalosCurtos.push(`${rot} (${fmtMin(ic)})`);
          // par, mas abaixo do mínimo do vínculo (ex.: CLT com só entrada/saída)
          if (b.length >= 2 && b.length % 2 === 0 && b.length < minBatidas) {
            incompletos.push(rot);
          }
          if (l.em_ausencia) emAusencia.push(rot);
          // dia já apurado no provedor (RHiD): o saldo é o da apuração dele — já
          // traz o turno da pessoa, tolerância, faltas, ímpares e os ajustes do RH.
          // É o número do app; recalcular aqui divergia (conferência 28/09/2026).
          if (l.saldo_provedor_seg != null) {
            const dp = l.saldo_provedor_seg;
            saldo += dp;
            if (dp > 0) extras += dp;
            else if (dp < 0) devendo += -dp;
            if (l.status === 'ok') diasOk += 1;
            return;
          }
          // só dia de trabalho COMPLETO ('ok') entra no saldo
          if (l.status !== 'ok' || b.length < 2) return;
          let worked = 0;
          for (let i = 0; i + 1 < b.length; i += 2) worked += h2s(b[i + 1]) - h2s(b[i]);
          // período justificado no provedor (ex.: tarde abonada na apuração do RHiD)
          // conta como jornada cumprida — senão o dia parcial vira débito
          worked += l.justificado_seg || 0;
          // sáb/dom nada era esperado: quem bateu ponto no fim de semana tem o
          // trabalhado TODO como crédito — descontar a jornada viraria débito.
          // Quem segue a config da empresa usa a jornada DO DIA (sexta pode ser
          // mais curta); jornada individual (Convenia/manual) vale todo dia.
          // dia da semana em que a pessoa não trabalha: nenhuma jornada esperada
          // (se vier batida, tudo é sobra)
          const esperadoDia = d.util && !folgaFixa(c.jornada_manual, d.semana)
            ? (c.esperado_seg != null
              ? (d.semana === 'Sex' && c.esperado_sexta_seg ? c.esperado_sexta_seg : esperado)
              : (d.esperado_seg ?? esperado)) : 0;
          // tolerância CLT: até ±10min no dia não conta; acima, conta o valor cheio
          let diff = worked - esperadoDia;
          if (Math.abs(diff) <= TOLERANCIA_SEG) diff = 0;
          saldo += diff;
          if (diff > 0) extras += diff;
          else if (diff < 0) devendo += -diff;   // horas que faltaram no dia
          diasOk += 1;
        });
        // banco acumulado oficial do provedor (PontoMais) no fim do mês;
        // quando existe, o saldo final = anterior + movimento do mês
        const banco = c.banco_seg != null ? Math.round(c.banco_seg) : null;
        // TiqueTaque manda só o acumulado ATÉ o mês anterior (soma do banco_horas
        // mensal da apuração deles); o total é esse anterior + o nosso mês
        const anteriorInformado = c.banco_anterior_seg != null ? Math.round(c.banco_anterior_seg) : null;
        // Ajuste manual (planilha de conciliação / operador) vence o provedor; as
        // horas PAGAS no mês saem do total; o fechamento do mês anterior vira o
        // anterior deste (o backend já resolve isso em banco_manual.anterior_seg)
        const manual = c.banco_manual || null;
        const anteriorManual = manual?.anterior_seg != null ? Math.round(manual.anterior_seg) : null;
        const pagasSeg = manual?.pagas_seg ? Math.round(manual.pagas_seg) : 0;
        const anteriorSeg = anteriorManual != null ? anteriorManual
          : (banco != null ? banco - saldo : anteriorInformado);
        const anteriorOrigem = anteriorManual != null ? (manual.anterior_origem || 'manual')
          : (banco != null || anteriorInformado != null ? 'provedor' : null);
        const totalSeg = (anteriorSeg != null ? anteriorSeg + saldo : saldo) - pagasSeg;
        let situacao;
        if (c.registra_ponto === false) situacao = 'nao_registra';
        else if (temBatida) situacao = totalSeg >= 0 ? 'credor' : 'devedor';
        else if (c.ausencia?.tipo === 'ferias') situacao = 'ferias';
        else if (c.ausencia?.tipo === 'afastamento') situacao = 'afastamento';
        else situacao = 'sem_batidas';
        const trabalhou = situacao === 'credor' || situacao === 'devedor';
        return {
          nome: c.nome, temBatida, diasOk, dias: c.dias || {},
          esperadoSeg: esperado,
          esperadoSextaSeg: c.esperado_sexta_seg || null,
          jornadaConvenia: c.esperado_seg != null,
          jornadaManual: c.jornada_manual || null,
          cpf: c.cpf || null,
          pontomais_id: c.pontomais_id,
          departamento: (c.departamento || '').trim() || SEM_DEP,
          ausencia: c.ausencia || null, situacao, trabalhou,
          nao_bateu: naoBateu,
          impar: impares,
          folgas,
          abonos: abonosDele,
          incompletos,
          intervalos_curtos: intervalosCurtos,
          vinculo,
          batidas_minimas: minBatidas,
          em_ausencia: emAusencia,
          feriados_trabalhados: feriadosTrab,
          horas_extras: trabalhou ? fmtHoras(extras) : '—',
          horas_devendo: trabalhou ? fmtHoras(devendo) : '—',
          devendo_seg: trabalhou ? devendo : 0,
          saldo: trabalhou ? fmtSaldo(saldo) : '—',
          saldo_seg: trabalhou ? saldo : 0,
          // banco de horas NÃO some em férias/afastamento: o saldo anterior e o
          // total herdado continuam valendo. Só a coluna do MÊS fica '—' —
          // quem não trabalhou não gerou movimento.
          saldo_anterior: anteriorSeg != null ? fmtSaldo(anteriorSeg) : '—',
          saldo_total: (trabalhou || anteriorSeg != null) ? fmtSaldo(totalSeg) : '—',
          saldo_total_seg: (trabalhou || anteriorSeg != null) ? totalSeg : 0,
          tem_banco: banco != null || anteriorSeg != null,
          banco_manual: manual,
          anterior_origem: anteriorOrigem,
          // anterior digitado à mão diferente do que o provedor informa (RHiD por
          // relatório, TiqueTaque): o manual vale, mas a tela avisa (28/10/2026 —
          // 5 de 38 na Setho/Mercatto estavam digitados com sinal ou dia errado)
          anterior_provedor_seg: anteriorInformado,
          anterior_divergente: anteriorManual != null && manual.anterior_origem === 'manual'
            && anteriorInformado != null && Math.abs(anteriorManual - anteriorInformado) >= 60,
          anterior_seg: anteriorSeg,
          pagas_seg: pagasSeg,
          horas_pagas: pagasSeg ? fmtHoras(pagasSeg) : '—',
          critico: trabalhou && Math.abs(totalSeg) >= CRITICO_SEG,
        };
      })
      .sort((a, b) => (ordem[a.situacao] - ordem[b.situacao]) || (b.saldo_total_seg - a.saldo_total_seg));
  }, [dadosMes]);

  // departamentos existentes (para o filtro)
  const departamentos = useMemo(() => {
    const set = new Set(relatorio.map(r => r.departamento));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [relatorio]);

  const relatorioFiltrado = useMemo(
    () => (depFiltro ? relatorio.filter(r => r.departamento === depFiltro) : relatorio),
    [relatorio, depFiltro]);

  // ─── análise por IA (Gemini) ───
  const [resumo, setResumo] = useState('');
  const [gerandoResumo, setGerandoResumo] = useState(false);

  // ─── seleção por colaborador: o mesmo departamento pode ter gestores
  //     diferentes — marca quem vai no envio; nada marcado = todos os filtrados ───
  const [selecionados, setSelecionados] = useState(() => new Set());
  useEffect(() => { setSelecionados(new Set()); }, [clienteId, mes, depFiltro]);
  const alternarSelecao = (id) => {
    setSelecionados(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
    setResumo('');   // a análise vale para o conjunto — mudou, gera de novo
  };
  const todosMarcados = relatorioFiltrado.length > 0
    && relatorioFiltrado.every(r => selecionados.has(r.pontomais_id));
  const alternarTodos = () => {
    setSelecionados(todosMarcados
      ? new Set() : new Set(relatorioFiltrado.map(r => r.pontomais_id)));
    setResumo('');
  };
  const relatorioEnvio = useMemo(
    () => (selecionados.size > 0
      ? relatorioFiltrado.filter(r => selecionados.has(r.pontomais_id))
      : relatorioFiltrado),
    [relatorioFiltrado, selecionados]);
  // dá para enviar filtrando por departamento OU marcando colaboradores à mão
  const podeEscolherDestino = Boolean(depFiltro) || selecionados.size > 0;

  const payloadColaboradores = () => relatorioEnvio.map(r => ({
    nome: r.nome, horas_extras: r.horas_extras, horas_devendo: r.horas_devendo,
    saldo: r.saldo, saldo_txt: r.saldo,
    saldo_anterior: r.saldo_anterior, saldo_total: r.saldo_total, tem_banco: r.tem_banco,
    situacao: r.situacao, critico: r.critico, ate: r.ausencia?.ate || '',
    nao_bateu: r.nao_bateu,
    impar: r.impar,
    intervalos_curtos: r.intervalos_curtos,
    incompletos: r.incompletos,
    folgas: r.folgas,
    vinculo: r.vinculo,
    batidas_minimas: r.batidas_minimas,
    em_ausencia: r.em_ausencia,
    feriados_trabalhados: r.feriados_trabalhados,
    alteracoes: altInfo.disponivel ? altDe(r.pontomais_id) : null,
  }));

  const gerarResumo = () => {
    if (relatorioEnvio.length === 0) { setMensagem('Sem dados para analisar'); return; }
    setGerandoResumo(true);
    axios.post(`${API}/api/bpo-ponto/${clienteId}/fechamento/resumo/`, {
      cliente: clienteNome, departamento: depFiltro,
      mes_rotulo: dadosMes?.mes_rotulo, periodo: dadosMes?.mes_rotulo,
      alteracoes_rotulo: altInfo.disponivel ? altInfo.rotulo : '',
      colaboradores: payloadColaboradores(),
    }, { headers: headers() })
      .then((res) => {
        setResumo(res.data.resumo || '');
        setMensagem('Análise gerada pela IA');
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível gerar a análise'}`))
      .finally(() => setGerandoResumo(false));
  };

  const payloadEnvio = () => ({
    operador_id: operadorId,
    gestor_nome: gestorNome, gestor_email: gestorEmail,
    departamento: depFiltro,
    mes_rotulo: dadosMes?.mes_rotulo, periodo: dadosMes?.mes_rotulo,
    resumo,
    alteracoes_rotulo: altInfo.disponivel ? altInfo.rotulo : '',
    colaboradores: payloadColaboradores(),
  });
  const validarEnvio = () => {
    if (!podeEscolherDestino) {
      setMensagem('Selecione um departamento ou marque colaboradores para enviar'); return false;
    }
    if (!gestorEmail.trim() || gestorEmail.indexOf('@') < 0) {
      setMensagem('Informe o e-mail do gestor'); return false;
    }
    if (!operadorId) { setMensagem('Escolha o operador que envia'); return false; }
    if (relatorioEnvio.length === 0) { setMensagem('Sem dados para enviar'); return false; }
    if (!resumo.trim()) {
      setMensagem('Gere a análise do ponto antes de enviar — o e-mail vai com ela.'); return false;
    }
    return true;
  };
  // 1ª etapa: prévia — o backend monta o e-mail e devolve sem enviar
  const [previaEmail, setPreviaEmail] = useState(null);   // {assunto, corpo_html, de, para, cc, bcc, colaboradores}
  // edição na prévia: o iframe vira editável (designMode) e o HTML editado vai no envio
  const [editandoPrevia, setEditandoPrevia] = useState(false);
  const previaRef = useRef(null);
  const ativarEdicaoPrevia = () => {
    const doc = previaRef.current?.contentDocument;
    if (!doc) return;
    doc.designMode = 'on';
    doc.body.style.outline = '2px dashed #00b5cc';
    doc.body.style.outlineOffset = '-2px';
    setEditandoPrevia(true);
    setMensagem('Clique no texto do e-mail e edite direto; o que você mudar vai no envio.');
  };
  const htmlEditado = () => {
    const doc = previaRef.current?.contentDocument;
    if (!doc || !editandoPrevia) return null;
    doc.body.style.outline = '';
    doc.body.style.outlineOffset = '';
    return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
  };
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);
  const abrirPreviaEmail = () => {
    if (!validarEnvio()) return;
    setCarregandoPrevia(true);
    axios.post(`${API}/api/bpo-ponto/${clienteId}/fechamento/enviar/`, { ...payloadEnvio(), previa: true }, { headers: headers() })
      .then((res) => { setEditandoPrevia(false); setPreviaEmail(res.data); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível montar a prévia do e-mail'}`))
      .finally(() => setCarregandoPrevia(false));
  };
  // 2ª etapa: o envio de verdade só sai de dentro do modal de prévia
  const enviar = () => {
    if (!validarEnvio()) return;
    setEnviando(true);
    const editado = htmlEditado();
    axios.post(`${API}/api/bpo-ponto/${clienteId}/fechamento/enviar/`, {
      ...payloadEnvio(),
      ...(previaEmail?.assunto ? { assunto: previaEmail.assunto } : {}),
      ...(editado ? { corpo_html: editado } : {}),
    }, { headers: headers() })
      .then((res) => { setMensagem(`${res.data.mensagem}`); setPreviaEmail(null); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível enviar'}`))
      .finally(() => setEnviando(false));
  };

  const salvarGestor = () => {
    if (!clienteId) return;
    if (!depFiltro) { setMensagem('Selecione um departamento para salvar o gestor'); return; }
    axios.patch(`${API}/api/bpo-ponto/${clienteId}/fechamento/gestor/`,
      { departamento: depFiltro, gestor_nome: gestorNome, gestor_email: gestorEmail }, { headers: headers() })
      .then((res) => { setGestores(res.data.gestores || []); setMensagem('Gestor do departamento salvo'); })
      .catch(() => setMensagem('Não foi possível salvar o gestor'));
  };

  const conta = (s) => relatorioFiltrado.filter(r => r.situacao === s).length;
  const credores = conta('credor');
  const devedores = conta('devedor');
  const emFerias = conta('ferias') + conta('afastamento');
  const naoBate = conta('nao_registra') + conta('sem_batidas');
  const totalFaltas = relatorioFiltrado.reduce((n, r) => n + r.nao_bateu.length, 0);
  const totalImpar = relatorioFiltrado.reduce((n, r) => n + r.impar.length, 0);
  const totalIncompleto = relatorioFiltrado.reduce(
    (n, r) => n + (r.incompletos || []).length, 0);
  const totalIntervaloCurto = relatorioFiltrado.reduce(
    (n, r) => n + (r.intervalos_curtos || []).length, 0);
  const totalFolgas = relatorioFiltrado.reduce((n, r) => n + (r.folgas || []).length, 0);
  const totalFeriadoTrab = relatorioFiltrado.reduce((n, r) => n + (r.feriados_trabalhados || []).length, 0);
  const feriadosDoMes = (dadosMes?.dias || []).filter(d => d.feriado);
  const totalAlt = altInfo.disponivel
    ? relatorioFiltrado.reduce((n, r) => n + altDe(r.pontomais_id), 0) : 0;

  // ─── detalhe por pessoa: dia a dia (onde não bateu, falta/sobra) ───
  const [detalhe, setDetalhe] = useState(null);   // linha do relatório

  // ─── correção da jornada: a Convenia é a fonte, mas o monthly_hours de lá às
  // vezes está errado. O ajuste fica no nosso banco e vence o do espelho.
  const [jornadaEdit, setJornadaEdit] = useState(null);  // {cpf, nome, mensal, atual}

  // ─── banco de horas manual: saldo anterior / horas pagas / fechar mês ───
  const [bancoEdit, setBancoEdit] = useState(null);   // {anterior, pagas, obs}
  const [salvandoBanco, setSalvandoBanco] = useState(false);
  const segParaHHMM = (seg) => {
    if (seg == null) return '';
    const a = Math.abs(Math.round(seg));
    return `${seg < 0 ? '-' : ''}${Math.floor(a / 3600)}:${String(Math.floor((a % 3600) / 60)).padStart(2, '0')}`;
  };
  // grava e atualiza o colaborador em memória (o relatório é derivado de dadosMes)
  const salvarBanco = (cpf, corpo) => {
    if (!cpf) { setMensagem('❌ Sem CPF no cadastro — não dá para ajustar o banco'); return; }
    setSalvandoBanco(true);
    axios.post(`${API}/api/bpo-ponto/${clienteId}/banco-manual/`, { cpf, mes, ...corpo }, { headers: headers() })
      .then((res) => {
        setMensagem(`✅ ${res.data.mensagem}`);
        setBancoEdit(null);
        setDadosMes((prev) => prev ? ({
          ...prev,
          colaboradores: (prev.colaboradores || []).map(c => (c.cpf === cpf ? { ...c, banco_manual: res.data.banco_manual } : c)),
        }) : prev);
      })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar o ajuste'}`))
      .finally(() => setSalvandoBanco(false));
  };
  const [salvandoJornada, setSalvandoJornada] = useState(false);

  const segParaMensal = (diaSeg) => {
    const mensal = Math.round((diaSeg || 0) * 25);   // mesmo divisor do backend
    return `${Math.floor(mensal / 3600)}:${String(Math.floor((mensal % 3600) / 60)).padStart(2, '0')}`;
  };

  // O operador informa o HORÁRIO (entrada/intervalo/saída), como está no
  // contrato — a diária e a mensal saem daí. Pré-preenche com o ajuste manual
  // se houver; senão com o horário da empresa.
  const abrirJornada = (d) => {
    const jm = d.jornadaManual || {};
    const cfg = dadosMes?.config || {};
    setJornadaEdit({
      cpf: d.cpf,
      nome: d.nome,
      manual: Boolean(d.jornadaManual),
      entrada: jm.entrada || cfg.entrada || '08:00',
      intervalo_inicio: jm.entrada ? (jm.intervalo_inicio || '') : (cfg.intervalo_inicio || ''),
      intervalo_fim: jm.entrada ? (jm.intervalo_fim || '') : (cfg.intervalo_fim || ''),
      saida: jm.saida || cfg.saida || '18:00',
      sexta_saida: jm.sexta_saida || '',
      dias_folga: jm.dias_folga || [],
      // ajuste antigo só com a mensal: mostra o que vale hoje
      mensalAtual: segParaMensal(d.esperadoSeg),
    });
  };

  // diária em segundos a partir dos horários do modal (prévia ao vivo)
  const jornadaSeg = (j, saidaCampo = 'saida') => {
    if (!j?.entrada || !j?.[saidaCampo]) return null;
    let seg = h2s(j[saidaCampo]) - h2s(j.entrada);
    if (j.intervalo_inicio && j.intervalo_fim) seg -= (h2s(j.intervalo_fim) - h2s(j.intervalo_inicio));
    return seg > 0 ? seg : null;
  };

  // Folga / não cobrar: tira o dia da conta de faltas sem precisar cobrar o
  // colaborador. 'não cobrar' exige motivo — o backend recusa sem ele.
  const [marcandoDia, setMarcandoDia] = useState('');
  const marcarDia = (cpf, iso, tipo, obs) => {
    if (!cpf) {
      setMensagem('❌ Sem CPF no cadastro deste colaborador — não dá para marcar o dia');
      return;
    }
    setMarcandoDia(iso);
    const token = localStorage.getItem('access');
    const corpo = tipo ? { cpf, data: iso, tipo, obs } : { cpf, data: iso, remover: true };
    axios
      .post(`${API}/api/bpo-ponto/${clienteId}/abono/`, corpo,
        { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setMensagem(`✅ ${res.data.mensagem}`);
        const novo = res.data.marcado ? res.data.abono : null;
        // Atualiza só o que mudou. Recarregar o mês aqui fechava o modal e
        // fazia a tela inteira piscar a cada clique — o relatório já é
        // derivado de dadosMes, então mexer no mapa basta para recalcular.
        setDadosMes((prev) => {
          if (!prev) return prev;
          const mapa = { ...(prev.abonos || {}) };
          const doCpf = { ...(mapa[cpf] || {}) };
          if (novo) doCpf[iso] = novo; else delete doCpf[iso];
          if (Object.keys(doCpf).length) mapa[cpf] = doCpf; else delete mapa[cpf];
          return { ...prev, abonos: mapa };
        });
        // o modal fica aberto: o operador costuma marcar vários dias seguidos
        setDetalhe((prev) => {
          if (!prev) return prev;
          const ab = { ...(prev.abonos || {}) };
          if (novo) ab[iso] = novo; else delete ab[iso];
          return { ...prev, abonos: ab };
        });
      })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível marcar o dia'}`))
      .finally(() => setMarcandoDia(''));
  };

  // Kairos não tem API: as batidas vêm do Excel "Relatório de Ponto do
  // Funcionário". Importa aqui mesmo (mesmo endpoint do painel de Ponto) e
  // recarrega o mês — sem obrigar a ir para a outra tela.
  const fileKairosRef = useRef(null);
  const [importandoKairos, setImportandoKairos] = useState(false);
  const importarKairos = (ev) => {
    const file = ev.target.files && ev.target.files[0];
    if (!file) return;
    setImportandoKairos(true);
    setMensagem('Lendo o Excel do Kairos — extraindo as batidas...');
    const fd = new FormData();
    fd.append('arquivo', file);
    axios.post(`${API}/api/bpo-ponto/${clienteId}/kairos/importar/`, fd, { headers: headers() })
      .then((res) => {
        const p = res.data;
        setMensagem(`✅ ${p.mensagem}${p.periodo ? ` — período ${p.periodo.de} a ${p.periodo.ate}` : ''}`);
        carregarRef.current();
      })
      .catch((error) => setMensagem(`❌ ${error.response?.data?.erro || 'Falha ao importar o Excel'}`))
      .finally(() => {
        setImportandoKairos(false);
        if (fileKairosRef.current) fileKairosRef.current.value = '';
      });
  };

  // remover=true volta para a jornada da Convenia
  const salvarJornada = (remover = false) => {
    if (!jornadaEdit?.cpf) return;
    if (!remover && (!jornadaEdit.entrada || !jornadaEdit.saida)) {
      setMensagem('❌ Informe entrada e saída'); return;
    }
    setSalvandoJornada(true);
    const token = localStorage.getItem('access');
    const corpo = remover ? { cpf: jornadaEdit.cpf, remover: true } : {
      cpf: jornadaEdit.cpf, entrada: jornadaEdit.entrada, saida: jornadaEdit.saida,
      intervalo_inicio: jornadaEdit.intervalo_inicio || '', intervalo_fim: jornadaEdit.intervalo_fim || '',
      sexta_saida: jornadaEdit.sexta_saida || '',
      dias_folga: jornadaEdit.dias_folga || [],
    };
    axios
      .post(`${API}/api/bpo-ponto/${clienteId}/jornada/`, corpo,
        { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setMensagem(`✅ ${res.data.mensagem}`);
        setJornadaEdit(null);
        setDetalhe(null);     // os totais mudam: reabre com o número novo
        carregar();
      })
      .catch((err) => setMensagem(
        `❌ ${err.response?.data?.mensagem || 'Não foi possível ajustar a jornada'}`))
      .finally(() => setSalvandoJornada(false));
  };
  const STATUS_DIA = {
    ok: { rotulo: 'OK', cor: '#116b47' },
    sem_batida: { rotulo: 'Não bateu', cor: '#a11d2b' },
    impar: { rotulo: 'Batida ímpar', cor: '#8a6d00' },
    feriado: { rotulo: 'Feriado', cor: '#00205b' },
    abonado: { rotulo: 'Folga / não cobrar', cor: '#0a6ea1' },
    incompleto: { rotulo: 'Sem intervalo', cor: '#b5560e' },
    trabalhando: { rotulo: 'Trabalhando', cor: '#116b47' },
    folga: { rotulo: 'Folga / DSR', cor: '#6c757d' },
    isento: { rotulo: 'Não registra', cor: '#6c757d' },
    ferias: { rotulo: 'Férias', cor: '#0a6ea1' },
    afastamento: { rotulo: 'Afastamento', cor: '#7a3b9e' },
    fora: { rotulo: '—', cor: '#adb5bd' },
    desligado: { rotulo: 'Desligado', cor: '#6c757d' },
  };
  const detalheDias = useMemo(() => {
    if (!detalhe || !dadosMes) return [];
    const esperado = detalhe.esperadoSeg != null ? detalhe.esperadoSeg : (dadosMes.esperado_seg || 0);
    return (dadosMes.dias || []).map(d => {
      const l = detalhe.dias?.[d.iso] || { batidas: [], status: 'fora' };
      const b = l.batidas || [];
      let worked = 0;
      for (let i = 0; i + 1 < b.length; i += 2) worked += h2s(b[i + 1]) - h2s(b[i]);
      worked += l.justificado_seg || 0;   // período abonado no provedor
      // só dia completo tem falta/sobra; dentro da tolerância de 10min conta zero
      // (fim de semana: esperado 0 — o trabalhado é todo sobra, igual ao saldo).
      // Config da empresa: jornada do dia (sexta pode ser menor); individual: fixa.
      const esperadoNoDia = folgaFixa(detalhe.jornadaManual, d.semana) ? 0
        : (detalhe.jornadaConvenia || detalhe.jornadaManual)
          ? (d.semana === 'Sex' && detalhe.esperadoSextaSeg ? detalhe.esperadoSextaSeg : esperado)
          : (d.esperado_seg ?? esperado);
      let saldoDia = l.status === 'ok' ? worked - (d.util ? esperadoNoDia : 0) : null;
      if (saldoDia != null && Math.abs(saldoDia) <= TOLERANCIA_SEG) saldoDia = 0;
      // dia apurado no RHiD: vale o saldo da apuração (o do app)
      if (l.saldo_provedor_seg != null) saldoDia = l.saldo_provedor_seg;
      // feriado vence o status do provedor: não bateu em feriado não é falta.
      // 'incompleto' = bateu, mas abaixo do mínimo do vínculo (intervalo).
      const minB = batidasMinimas(detalhe.vinculo);
      const ab = (detalhe.abonos || {})[d.iso] || null;
      let st = l.status;
      if (ab && b.length === 0) st = 'abonado';
      else if (d.feriado && b.length === 0) st = 'feriado';
      else if (b.length >= 2 && b.length % 2 === 0 && b.length < minB) st = 'incompleto';
      return { ...d, batidas: b, status: st, feriado: d.feriado, abono: ab,
               em_ausencia: l.em_ausencia, worked, saldoDia, intervaloCurto: intervaloCurto(b) };
    });
  }, [detalhe, dadosMes]);

  // o modal aberto acompanha o relatório recalculado (folga, jornada, banco)
  useEffect(() => {
    if (!detalhe) return;
    const novo = relatorio.find(r => r.pontomais_id === detalhe.pontomais_id);
    if (novo && novo !== detalhe) setDetalhe(novo);
  }, [relatorio]); // eslint-disable-line react-hooks/exhaustive-deps

  const clienteSel = clientes.find(c => String(c.id) === String(clienteId));
  const sistemaRotulo = (clienteSel?.sistemas || []).length > 1
    ? (clienteSel.sistemas.find(x => x.tipo === (sistema || dadosMes?.provedor))?.rotulo || '')
    : '';
  const clienteNome = (clienteSel?.nome || dadosMes?.cliente || '') + (sistemaRotulo ? ` · ${sistemaRotulo}` : '');

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho (mesmo padrão de Admissões) */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-circle-check header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Resumo de Ponto
              {clienteNome && <span className="text-muted"> · {clienteNome}</span>}
            </h2>
          </div>
        </div>

      {/* filtros */}
      <div className="row g-3 mb-3">
        <div className="col-md-5">
          <label className="form-label fw-bold small">Empresa</label>
          <select className="form-select" value={clienteId ? `${clienteId}|${sistema}` : ''}
            onChange={(e) => {
              // valor "id|sistema" (sistema vazio = o preferido do backend)
              const [id, sis] = e.target.value.split('|');
              setClienteId(id || ''); setSistema(sis || ''); setDadosMes(null); setDepFiltro('');
            }}>
            <option value="">Selecione a empresa...</option>
            {clientes.flatMap(c => ((c.sistemas || []).length > 1
              ? c.sistemas.map(sis => (
                <option key={`${c.id}|${sis.tipo}`} value={`${c.id}|${sis.tipo}`}>{c.nome} · {sis.rotulo}</option>
              ))
              : [<option key={`${c.id}|`} value={`${c.id}|`}>{c.nome}</option>]))}
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold small">Departamento</label>
          <select className="form-select" value={depFiltro}
            onChange={(e) => setDepFiltro(e.target.value)} disabled={!dadosMes}>
            <option value="">Todos os departamentos</option>
            {departamentos.map(d => {
              const temGestor = gestores.some(g => g.departamento === d && g.gestor_email);
              return <option key={d} value={d}>{d}{temGestor ? ' ' : ''}</option>;
            })}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-bold small">Mês de referência</label>
          <input type="month" className="form-control" value={mes}
            onChange={(e) => setMes(e.target.value)} />
        </div>
      </div>

      {!clienteId ? (
        <div className="alert alert-light border text-center text-muted">
          <i className="fas fa-arrow-up me-2"></i>Escolha uma empresa para ver o resumo de ponto.
        </div>
      ) : (sincronizando || carregando) ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <p className="text-muted mt-2">
            {sincronizando
              ? 'Atualizando colaboradores e batidas direto do provedor de ponto...'
              : 'Calculando o banco de horas...'}
          </p>
        </div>
      ) : !dadosMes ? (
        <div className="alert alert-danger">Não foi possível carregar o ponto desta empresa neste mês.</div>
      ) : (
        <>
          {/* Kairos/Aghora: as batidas vêm de planilha importada — o resumo só
              está certo se a planilha do mês estiver atualizada. Aviso sempre
              visível antes de gerar/enviar (pedido do Eleandro, 24/08/2026). */}
          {['kairos', 'aghora'].includes(dadosMes.provedor) && (
            dadosMes.precisa_importar ? (
              <div className="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-3"
                style={{ borderLeft: '6px solid #dc3545' }}>
                <div style={{ fontSize: '1.05rem' }}>
                  <i className="fas fa-triangle-exclamation me-2 fa-lg"></i>
                  <b>ATENÇÃO:</b> {dadosMes.provedor === 'aghora' ? 'o Ponto Aghora' : 'o Kairos'} não
                  tem API — <u>não gere nem envie este resumo</u> antes de
                  <b> exportar a planilha no portal e importá-la no painel de Ponto</b>. Sem isso,
                  o mês aparece sem nenhuma batida.
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {dadosMes.provedor === 'kairos' && (
                    <label className={`btn btn-danger fw-bold mb-0 ${importandoKairos ? 'disabled' : ''}`}
                      title="Excel 'Relatório de Ponto do Funcionário' exportado do Kairos">
                      <i className={`fas ${importandoKairos ? 'fa-spinner fa-spin' : 'fa-file-excel'} me-2`}></i>
                      {importandoKairos ? 'Importando...' : 'Importar Excel do Kairos aqui'}
                      <input ref={fileKairosRef} type="file" accept=".xlsx,.xls" hidden disabled={importandoKairos}
                        onChange={importarKairos} />
                    </label>
                  )}
                  <Link className="btn btn-outline-danger fw-bold"
                    to={`/BpoRh/Ponto/cliente/${clienteId}?sistema=${dadosMes.provedor}`}>
                    <i className="fas fa-upload me-2"></i>Importar no painel de Ponto
                  </Link>
                </div>
              </div>
            ) : (
              <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 py-2"
                style={{ borderLeft: '6px solid #ffc107' }}>
                <div>
                  <i className={`fas ${dadosMes.provedor === 'aghora' ? 'fa-file-csv' : 'fa-file-excel'} me-2`}></i>
                  <b>Antes de gerar/enviar o resumo:</b> as batidas
                  de {dadosMes.provedor === 'aghora' ? 'Ponto Aghora' : 'Kairos'} vêm de
                  <b> planilha importada</b> — última importação em <b>{dadosMes.importado_em || '—'}</b>.
                  Exporte a planilha atualizada no portal e reimporte no painel de Ponto para o
                  resumo sair com o mês completo.
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {dadosMes.provedor === 'kairos' && (
                    <label className={`btn btn-dark btn-sm mb-0 ${importandoKairos ? 'disabled' : ''}`}
                      title="Excel 'Relatório de Ponto do Funcionário' exportado do Kairos">
                      <i className={`fas ${importandoKairos ? 'fa-spinner fa-spin' : 'fa-file-excel'} me-2`}></i>
                      {importandoKairos ? 'Importando...' : 'Reimportar Excel aqui'}
                      <input ref={fileKairosRef} type="file" accept=".xlsx,.xls" hidden disabled={importandoKairos}
                        onChange={importarKairos} />
                    </label>
                  )}
                  <Link className="btn btn-outline-dark btn-sm"
                    to={`/BpoRh/Ponto/cliente/${clienteId}?sistema=${dadosMes.provedor}`}>
                    <i className="fas fa-upload me-2"></i>Reimportar no painel
                  </Link>
                </div>
              </div>
            )
          )}
          {/* resumo */}
          <div className="d-flex gap-2 flex-wrap mb-3">
            <span className="badge fs-6" style={{ background: '#00205b' }}>
              <i className="fas fa-calendar-days me-1"></i> {dadosMes.mes_rotulo}
            </span>
            {depFiltro && (
              <span className="badge fs-6" style={{ background: '#00b5cc' }}>
                <i className="fas fa-sitemap me-1"></i> {depFiltro}
              </span>
            )}
            <span className="badge bg-secondary fs-6">{relatorioFiltrado.length} colaboradores</span>
            {feriadosDoMes.length > 0 && (
              <span className="badge fs-6"
                style={{ background: '#eef2f7', color: '#00205b', border: '1px solid #d6dee8' }}
                title={`Feriados desta competência (não contam como dia útil e não cobram batida): ${
                  feriadosDoMes.map(f => `${String(f.dia).padStart(2, '0')} ${f.feriado}`).join(' · ')}`}>
                <i className="fas fa-calendar-star me-1"></i>
                {feriadosDoMes.length} feriado{feriadosDoMes.length > 1 ? 's' : ''}
                {': '}
                {feriadosDoMes.map(f => `${String(f.dia).padStart(2, '0')}/${
                  (dadosMes.mes || '').slice(5)}`).join(', ')}
              </span>
            )}
            {totalFolgas > 0 && (
              <span className="badge fs-6"
                style={{ background: '#e3f2fd', color: '#0a6ea1', border: '1px solid #0a6ea1' }}
                title="Dias marcados pelo operador como folga ou 'não cobrar' — saem da conta de faltas">
                <i className="fas fa-mug-hot me-1"></i>
                {totalFolgas} dia(s) abonado(s)
              </span>
            )}
            {totalFeriadoTrab > 0 && (
              <span className="badge fs-6"
                style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #dc3545' }}
                title="Batidas em dia de feriado: não há jornada a cumprir, então as horas entram como sobra — confira se o trabalho foi autorizado (hora extra em feriado é paga em dobro ou compensada)">
                <i className="fas fa-star me-1"></i>
                {totalFeriadoTrab} trabalho(s) em feriado
              </span>
            )}
            {totalIncompleto > 0 && (
              <span className="badge fs-6"
                style={{ background: '#fff8e6', color: '#8a6d00', border: '1px solid #f1be48' }}
                title="Dia trabalhado com menos batidas do que o vínculo exige — CLT precisa de 4 (com intervalo); aprendiz e estágio, 2">
                <i className="fas fa-clock-rotate-left me-1"></i>
                {totalIncompleto} dia(s) sem o intervalo marcado
              </span>
            )}
            {totalIntervaloCurto > 0 && (
              <span className="badge fs-6"
                style={{ background: '#fff1e6', color: '#b5560e', border: '1px solid #f0a868' }}
                title="Dias com mais de 6h trabalhadas e intervalo (almoço) menor que 1h — CLT art. 71">
                <i className="fas fa-utensils me-1"></i>
                {totalIntervaloCurto} dia(s) com intervalo menor que 1h
              </span>
            )}
            <span className="badge fs-6" style={{ background: '#e6f7ef', color: '#116b47', border: '1px solid #1fa971' }}>
              {credores} credores
            </span>
            <span className="badge fs-6" style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #dc3545' }}>
              {devedores} devedores
            </span>
            {emFerias > 0 && (
              <span className="badge fs-6" style={{ background: '#e3f2fd', color: '#0a6ea1', border: '1px solid #0a6ea1' }}>
                {emFerias} férias/afastados
              </span>
            )}
            {naoBate > 0 && (
              <span className="badge fs-6" style={{ background: '#f1f3f5', color: '#6c757d', border: '1px solid #ced4da' }}>
                {naoBate} sem batidas
              </span>
            )}
            {totalFaltas > 0 && (
              <span className="badge fs-6" style={{ background: '#fff8e6', color: '#8a6d00', border: '1px solid #f1be48' }}>
                <i className="fas fa-triangle-exclamation me-1"></i>{totalFaltas} dia(s) sem registro
              </span>
            )}
            {totalImpar > 0 && (
              <span className="badge fs-6" style={{ background: '#fff1e6', color: '#b5560e', border: '1px solid #f0a868' }}>
                <i className="fas fa-clock-rotate-left me-1"></i>{totalImpar} batida(s) ímpar
              </span>
            )}
            {totalAlt > 0 && (
              <span className="badge fs-6" style={{ background: '#e9edf5', color: '#00205b', border: '1px solid #c7d2e2' }}
                title={altInfo.rotulo}>
                <i className="fas fa-pen-to-square me-1"></i>{totalAlt} alteração(ões) de ponto
              </span>
            )}
          </div>
          {altInfo.disponivel && (
            <div className="text-muted small mb-3" style={{ marginTop: '-8px' }}>
              <i className="fas fa-circle-info me-1"></i>
              Coluna <b>Alterações</b>: {altInfo.rotulo.toLowerCase()} no período.
            </div>
          )}

          {/* análise por IA */}
          <div className="bg-white rounded shadow-sm p-3 mb-3" style={{ borderLeft: '4px solid #00b5cc' }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="fw-bold mb-0">
                <i className="fas fa-wand-magic-sparkles me-2" style={{ color: '#00b5cc' }}></i>
                Análise do ponto (IA)
                {depFiltro && <span className="text-muted small"> · {depFiltro}</span>}
                {selecionados.size > 0 && (
                  <span className="text-muted small"> · {relatorioEnvio.length} selecionado(s)</span>
                )}
              </h6>
              <button className="btn btn-sm btn-outline-primary" onClick={gerarResumo}
                disabled={gerandoResumo || relatorioEnvio.length === 0}>
                <i className={`fas ${gerandoResumo ? 'fa-spinner fa-spin' : 'fa-robot'} me-1`}></i>
                {gerandoResumo ? 'Gerando...' : (resumo ? 'Gerar novamente' : 'Gerar análise')}
              </button>
            </div>
            {resumo ? (
              <div className="mt-3 p-3 rounded" style={{ background: '#f4f8fc', whiteSpace: 'pre-wrap',
                fontSize: '.92rem', lineHeight: 1.6, color: '#212529' }}>
                {resumo}
              </div>
            ) : (
              <p className="text-muted small mb-0 mt-2">
                Gere um resumo executivo, diagnósticos e recomendações do banco de horas.
                Vai junto no e-mail ao gestor.
              </p>
            )}
          </div>

          {/* tabela */}
          <div className="table-responsive bg-white rounded shadow-sm mb-4">
            <table className="table table-hover align-middle mb-0">
              <thead style={{ background: '#f8f9fa' }}>
                <tr>
                  <th className="text-center" style={{ width: '36px' }}
                    title="Marcar quem vai no envio — o mesmo departamento pode ter gestores diferentes">
                    <input type="checkbox" className="form-check-input"
                      checked={todosMarcados} onChange={alternarTodos} />
                  </th>
                  <th><i className="fas fa-user me-2"></i>Colaborador</th>
                  {!depFiltro && <th><i className="fas fa-sitemap me-2"></i>Departamento</th>}
                  <th className="text-center" title="Soma dos dias acima da jornada (tolerância de 10min/dia)">
                    <i className="fas fa-plus me-1"></i>Horas extras
                  </th>
                  <th className="text-center" title="Soma das horas que faltaram nos dias abaixo da jornada (tolerância de 10min/dia)">
                    <i className="fas fa-minus me-1"></i>Horas devendo
                  </th>
                  <th className="text-center" title="Saldo acumulado que veio dos meses anteriores (banco do provedor)">
                    Saldo anterior
                  </th>
                  <th className="text-center" title="Movimento deste mês: extras − devendo">
                    Saldo do mês
                  </th>
                  <th className="text-center" title="Saldo anterior + saldo do mês (banco oficial do provedor)">
                    <i className="fas fa-scale-balanced me-1"></i>Saldo do banco
                  </th>
                  <th className="text-center" title="Dias sem batida, com nº ímpar de batidas, sem o intervalo marcado (CLT exige 4 batidas; aprendiz e estágio, 2) e com intervalo menor que 1h">
                    Pendências
                  </th>
                  <th className="text-center" title={altInfo.rotulo}>
                    <i className="fas fa-pen-to-square me-1"></i>Alterações
                  </th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {relatorioFiltrado.map((r) => {
                  const sf = SITUACAO_FMT[r.situacao] || SITUACAO_FMT.sem_batidas;
                  // cor pelo SINAL do saldo, não pelo tamanho: positivo verde, negativo
                  // vermelho. 'crítico' (acima de 15h) continua escrito no status.
                  // cinza-claro só quando NÃO há saldo nenhum; saldo herdado
                  // (férias/afastamento) fica em tinta normal, para ser lido
                  const corSaldo = !r.trabalhou
                    ? (r.saldo_total === '—' ? '#adb5bd' : '#212529')
                    : ((r.saldo_total_seg ?? r.saldo_seg ?? 0) < 0 ? '#a11d2b' : '#116b47');
                  const ateTxt = r.ausencia?.ate && (r.situacao === 'ferias' || r.situacao === 'afastamento')
                    ? ` até ${r.ausencia.ate}` : '';
                  return (
                    <tr key={r.pontomais_id} style={{ cursor: 'pointer',
                        background: selecionados.has(r.pontomais_id) ? '#eef6ff' : undefined }}
                      onClick={() => setDetalhe(r)}
                      title="Clique para ver o dia a dia (onde não bateu, falta/sobra)">
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="form-check-input"
                          checked={selecionados.has(r.pontomais_id)}
                          onChange={() => alternarSelecao(r.pontomais_id)} />
                      </td>
                      <td className="fw-semibold">
                        {r.nome}
                        <i className="fas fa-magnifying-glass ms-2 text-muted" style={{ fontSize: '.75rem' }}></i>
                      </td>
                      {!depFiltro && (
                        <td className="text-muted small">{r.departamento}</td>
                      )}
                      <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.horas_extras}</td>
                      <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums',
                        color: r.devendo_seg > 0 ? '#a11d2b' : undefined }}>
                        {r.horas_devendo}
                      </td>
                      <td className="text-center text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {r.saldo_anterior}
                        {r.anterior_divergente && (
                          <i className="fas fa-triangle-exclamation ms-1" style={{ color: '#b7791f' }}
                            title={`Digitado à mão: ${r.saldo_anterior}. O provedor de ponto informa ${fmtSaldo(r.anterior_provedor_seg)}. Confira no detalhe.`}></i>
                        )}
                      </td>
                      <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {r.saldo}
                      </td>
                      <td className="text-center fw-bold" style={{ color: corSaldo, fontVariantNumeric: 'tabular-nums' }}>
                        {r.saldo_total}
                      </td>
                      <td className="text-center">
                        <div className="d-flex flex-column align-items-center gap-1">
                          {r.nao_bateu.length > 0 && (
                            <span className="badge" style={{ background: '#fff8e6', color: '#8a6d00', border: '1px solid #f1be48' }}
                              title={`Sem registro: ${r.nao_bateu.join(', ')}`}>
                              {r.nao_bateu.length} sem registro
                            </span>
                          )}
                          {r.impar.length > 0 && (
                            <span className="badge" style={{ background: '#fff1e6', color: '#b5560e', border: '1px solid #f0a868' }}
                              title={`Batida ímpar: ${r.impar.join(', ')}`}>
                              {r.impar.length} ímpar
                            </span>
                          )}
                          {(r.intervalos_curtos || []).length > 0 && (
                            <span className="badge" style={{ background: '#fff1e6', color: '#b5560e', border: '1px solid #f0a868' }}
                              title={`Intervalo menor que 1h: ${r.intervalos_curtos.join(', ')}`}>
                              <i className="fas fa-utensils me-1"></i>{r.intervalos_curtos.length} intervalo &lt; 1h
                            </span>
                          )}
                          {r.em_ausencia.length > 0 && (
                            <span className="badge" style={{ background: '#f3e8fb', color: '#7a3b9e', border: '1px solid #7a3b9e' }}
                              title={`Bateu ponto estando de férias/afastado: ${r.em_ausencia.join(', ')} — confira se trabalhou mesmo`}>
                              {r.em_ausencia.length} em férias
                            </span>
                          )}
                          {(r.feriados_trabalhados || []).length > 0 && (
                            <span className="badge" style={{ background: '#fdeaec', color: '#a11d2b', border: '1px solid #dc3545' }}
                              title={`Trabalhou em feriado: ${r.feriados_trabalhados.join(', ')} — as horas entram como sobra; confira se foi autorizado`}>
                              <i className="fas fa-star me-1"></i>{r.feriados_trabalhados.length} em feriado
                            </span>
                          )}
                          {r.nao_bateu.length === 0 && r.impar.length === 0
                            && (r.intervalos_curtos || []).length === 0
                            && r.em_ausencia.length === 0 && (r.feriados_trabalhados || []).length === 0 && (
                            <span className="text-muted">—</span>
                          )}
                        </div>
                      </td>
                      <td className="text-center" title={altInfo.rotulo}>
                        {altInfo.disponivel === null ? (
                          <span className="text-muted small"><i className="fas fa-spinner fa-spin"></i></span>
                        ) : altInfo.disponivel === false ? (
                          <span className="text-muted small">n/d</span>
                        ) : altDe(r.pontomais_id) > 0 ? (
                          <span className="badge" style={{ background: '#e9edf5', color: '#00205b', border: '1px solid #c7d2e2' }}>
                            {altDe(r.pontomais_id)}×
                          </span>
                        ) : <span className="text-muted">—</span>}
                      </td>
                      <td className="text-center">
                        <span className="badge" style={{ background: sf.bg, color: sf.cor, border: `1px solid ${sf.borda}` }}>
                          {sf.rotulo}{r.critico ? ' (crítico)' : ''}{ateTxt}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {relatorioFiltrado.length === 0 && (
                  <tr><td colSpan={depFiltro ? 10 : 11} className="text-center text-muted py-4">
                    <i className="fas fa-inbox me-2"></i>Nenhum colaborador neste filtro.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* envio ao gestor do departamento */}
          <div className="bg-white rounded shadow-sm p-3">
            <h6 className="fw-bold mb-3">
              <i className="fas fa-paper-plane me-2 text-primary"></i>
              Enviar ao gestor {depFiltro ? <>do departamento <span className="text-primary">{depFiltro}</span></> : ''}
              {selecionados.size > 0 && (
                <span className="badge ms-2" style={{ background: '#eef6ff', color: '#0a58ca', border: '1px solid #9ec5fe' }}>
                  {relatorioEnvio.length} colaborador(es) selecionado(s)
                </span>
              )}
            </h6>
            {!podeEscolherDestino && (
              <div className="alert alert-warning py-2 small mb-3">
                <i className="fas fa-circle-info me-1"></i>
                Selecione um <b>departamento</b> acima ou <b>marque colaboradores</b> na tabela
                (útil quando o mesmo departamento tem gestores diferentes) para enviar.
              </div>
            )}
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small fw-bold">Gestor</label>
                <select className="form-select" value={gestorSel} onChange={escolherGestor}
                  disabled={!podeEscolherDestino}
                  title="Gestores da empresa (supervisores da Convenia + já cadastrados)">
                  <option value="">Selecione o gestor...</option>
                  {gestoresEmpresa.map((g, i) => (
                    <option key={`${g.nome}-${i}`} value={String(i)}>
                      {g.nome}{g.email ? ` — ${g.email}` : ' — sem e-mail'}
                    </option>
                  ))}
                  <option value="outro">Outro (digitar nome e e-mail)</option>
                </select>
                {gestorSel === 'outro' && (
                  <input type="text" className="form-control form-control-sm mt-1" value={gestorNome}
                    onChange={(e) => setGestorNome(e.target.value)}
                    placeholder="Nome do gestor" disabled={!podeEscolherDestino} />
                )}
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-bold">E-mail do gestor</label>
                <div className="input-group">
                  <input type="email" className="form-control" value={gestorEmail}
                    onChange={(e) => setGestorEmail(e.target.value)} placeholder="gestor@empresa.com.br"
                    disabled={!podeEscolherDestino} />
                  <button className="btn btn-outline-secondary" onClick={salvarGestor} disabled={!depFiltro}
                    title={depFiltro
                      ? 'Salvar o gestor deste departamento para as próximas vezes'
                      : 'O gestor salvo é por departamento — selecione um departamento para salvar'}>
                    <i className="fas fa-floppy-disk"></i>
                  </button>
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold">Enviar como</label>
                <select className="form-select" value={operadorId}
                  onChange={(e) => setOperadorId(e.target.value)}>
                  {operadores.map(o => (
                    <option key={o.id} value={o.id} disabled={!o.pronto}>
                      {o.nome}{!o.pronto ? ' (sem e-mail/senha)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* gestores já cadastrados */}
            {gestores.length > 0 && (
              <div className="mt-3">
                <div className="small fw-bold text-muted mb-1">Gestores cadastrados por departamento</div>
                <div className="d-flex flex-wrap gap-2">
                  {gestores.map(g => (
                    <span key={g.departamento} role="button"
                      onClick={() => setDepFiltro(g.departamento)}
                      className="badge d-inline-flex align-items-center gap-1"
                      style={{ background: '#eef2f7', color: '#00205b', border: '1px solid #d6dee8', cursor: 'pointer' }}
                      title="Clique para filtrar por este departamento">
                      <i className="fas fa-user-tie"></i>
                      <b>{g.departamento}</b>
                      <span className="text-muted">· {g.gestor_email || 'sem e-mail'}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end align-items-center gap-3 mt-3">
              {podeEscolherDestino && !resumo.trim() && (
                <span className="small" style={{ color: '#8a6d00' }}>
                  <i className="fas fa-wand-magic-sparkles me-1"></i>
                  Gere a <b>análise do ponto</b> lá em cima antes de enviar — ela vai no e-mail.
                </span>
              )}
              <button className="btn btn-success" onClick={abrirPreviaEmail}
                disabled={carregandoPrevia || enviando || !podeEscolherDestino || !gestorEmail.trim()
                  || relatorioEnvio.length === 0 || !resumo.trim()}
                title={!resumo.trim() ? 'Obrigatório gerar a análise do ponto antes de enviar'
                  : 'Mostra o e-mail como vai chegar ao gestor; o envio é confirmado na prévia'}>
                <i className={`fas ${carregandoPrevia ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>
                {carregandoPrevia ? 'Montando prévia...'
                  : `Ver prévia e enviar (${relatorioEnvio.length})`}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ─── MODAL: dia a dia do colaborador ─── */}
      {detalhe && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,.5)', zIndex: 9999 }}>
          {/* largura: a tabela do dia a dia ganhou colunas (situação, falta/sobra
              e a marcação de não cobrar com o motivo) e 680px espremia tudo.
              min(96vw) para não estourar em tela pequena. */}
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: 'min(1280px, 96vw)' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-clock me-2 text-primary"></i>{detalhe.nome}
                  </h5>
                  <small className="text-muted">
                    {detalhe.departamento} · {dadosMes?.mes_rotulo}
                    {' · '}jornada {fmtHoras(detalhe.esperadoSeg || 0)}/dia
                    {detalhe.jornadaManual
                      ? ' (ajustada à mão)'
                      : detalhe.jornadaConvenia ? ' (Convenia)' : ' (config da empresa)'}
                    {detalhe.cpf && (
                      <button type="button" className="btn btn-link btn-sm p-0 ms-1"
                        style={{ fontSize: '.75rem', textDecoration: 'none' }}
                        onClick={() => abrirJornada(detalhe)}
                        title="Corrigir a jornada deste colaborador">
                        <i className="fas fa-pen-to-square"></i>
                      </button>
                    )}
                    {' · '}{detalhe.diasOk} dia(s) trabalhado(s)
                    {detalhe.nao_bateu.length > 0 && (
                      <> · <b style={{ color: '#8a6d00' }}>{detalhe.nao_bateu.length} sem registro</b></>
                    )}
                    {detalhe.impar.length > 0 && (
                      <> · <b style={{ color: '#b5560e' }}>{detalhe.impar.length} batida ímpar</b></>
                    )}
                    {(detalhe.intervalos_curtos || []).length > 0 && (
                      <> · <b style={{ color: '#b5560e' }}>
                        <i className="fas fa-utensils me-1"></i>{detalhe.intervalos_curtos.length} dia(s) com intervalo menor que 1h
                      </b></>
                    )}
                    {(detalhe.feriados_trabalhados || []).length > 0 && (
                      <> · <b style={{ color: '#a11d2b' }}>
                        <i className="fas fa-star me-1"></i>trabalhou em feriado: {detalhe.feriados_trabalhados.join(', ')}</b></>
                    )}
                    {detalhe.em_ausencia.length > 0 && (
                      <> · <b style={{ color: '#7a3b9e' }}>
                        {detalhe.em_ausencia.length} dia(s) com ponto em férias/afastamento
                      </b></>
                    )}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setDetalhe(null)}></button>
              </div>
              <div className="modal-body">
                {/* a conta do banco, explícita: o que veio de antes + o mês = total */}
                {(() => {
                  const corSaldo = (txt) => (String(txt).startsWith('−') || String(txt).startsWith('-') ? '#a11d2b' : '#116b47');
                  const Caixa = ({ rotulo, valor, destaque, titulo }) => (
                    <div className="text-center px-3" title={titulo}>
                      <div className="small text-muted text-uppercase" style={{ fontSize: '.7rem', letterSpacing: '.4px' }}>{rotulo}</div>
                      <div className={destaque ? 'fs-4 fw-bold' : 'fs-5 fw-semibold'}
                        style={{ color: valor === '—' ? '#adb5bd' : corSaldo(valor), fontVariantNumeric: 'tabular-nums' }}>
                        {valor}
                      </div>
                    </div>
                  );
                  const Op = ({ s: sinal }) => <div className="fs-4 text-muted fw-light">{sinal}</div>;
                  const bm = detalhe.banco_manual || {};
                  const origemTxt = detalhe.anterior_origem === 'manual' ? 'ajustado à mão / planilha'
                    : detalhe.anterior_origem === 'fechamento' ? `fechamento do mês anterior${bm.fechado_por ? ` por ${bm.fechado_por}` : ''}`
                      : detalhe.anterior_origem === 'provedor' ? 'informado pelo provedor de ponto' : 'não informado';
                  const abrirEdicao = () => setBancoEdit({
                    anterior: detalhe.anterior_seg != null ? segParaHHMM(detalhe.anterior_seg) : '',
                    pagas: detalhe.pagas_seg ? segParaHHMM(detalhe.pagas_seg) : '',
                    obs: bm.obs || '',
                  });
                  const fechado = bm.final_seg != null;
                  const totalAtual = detalhe.trabalhou ? detalhe.saldo_total_seg : 0;
                  return (
                    <div className="rounded py-2 mb-3" style={{ background: '#f8f9fa', border: '1px solid #e9ecef' }}>
                      <div className="d-flex align-items-center justify-content-center flex-wrap gap-1">
                        <Caixa rotulo="Saldo mês anterior" valor={detalhe.saldo_anterior}
                          titulo={`Origem: ${origemTxt}. Clique no lápis para informar ou corrigir.`} />
                        <Op s="+" />
                        <Caixa rotulo={`Saldo de ${dadosMes?.mes_rotulo || 'do mês'}`} valor={detalhe.saldo}
                          titulo="Soma de falta/sobra dos dias trabalhados completos deste mês (tolerância de 10min/dia)" />
                        <Op s="−" />
                        <Caixa rotulo="Horas pagas no mês" valor={detalhe.horas_pagas}
                          titulo="Horas extras pagas em folha neste mês: saem do banco" />
                        <Op s="=" />
                        <Caixa rotulo="Saldo total (banco)" valor={detalhe.saldo_total} destaque
                          titulo="Saldo anterior + saldo do mês − horas pagas" />
                        <div className="d-flex flex-column gap-1 ms-3">
                          <button className="btn btn-sm btn-light border" onClick={abrirEdicao} disabled={!detalhe.cpf}
                            title="Informar/corrigir o saldo anterior, as horas pagas e a observação">
                            <i className="fas fa-pen me-1"></i>Ajustar
                          </button>
                          <button className={`btn btn-sm ${fechado ? 'btn-outline-success' : 'btn-success'}`}
                            disabled={salvandoBanco || !detalhe.cpf || !detalhe.trabalhou}
                            onClick={() => salvarBanco(detalhe.cpf, { saldo_final: segParaHHMM(totalAtual) })}
                            title="Grava o saldo total deste mês como saldo anterior do mês seguinte">
                            <i className={`fas ${fechado ? 'fa-rotate' : 'fa-lock'} me-1`}></i>{fechado ? 'Refechar mês' : 'Fechar mês'}
                          </button>
                        </div>
                      </div>
                      <div className="text-center small text-muted mt-1">
                        Saldo anterior: {origemTxt}
                        {bm.obs && <> · <i>{bm.obs}</i></>}
                        {fechado && (
                          <> · mês fechado com <b>{segParaHHMM(bm.final_seg)}</b>
                            {bm.final_seg !== totalAtual && <span className="text-warning"> (o total mudou depois: refeche)</span>}
                            {bm.autor && ` por ${bm.autor}`}{bm.em && ` em ${bm.em}`}</>
                        )}
                      </div>
                      {detalhe.anterior_divergente && (
                        <div className="text-center small mt-1" style={{ color: '#8a6d00' }}>
                          <i className="fas fa-triangle-exclamation me-1"></i>
                          O saldo anterior digitado ({detalhe.saldo_anterior}) é diferente do informado pelo provedor
                          de ponto (<b>{fmtSaldo(detalhe.anterior_provedor_seg)}</b>). Confira: para usar o do provedor,
                          em Ajustar apague o saldo anterior e salve.
                        </div>
                      )}
                      {bancoEdit && (
                        <div className="d-flex align-items-end justify-content-center flex-wrap gap-2 mt-2 px-3">
                          <div>
                            <label className="form-label small mb-0">Saldo anterior (HH:MM, − para negativo)</label>
                            <input type="text" className="form-control form-control-sm" style={{ width: 150 }} autoFocus
                              placeholder="ex.: 102:38 ou -02:32" value={bancoEdit.anterior}
                              onChange={(e) => setBancoEdit(b => ({ ...b, anterior: e.target.value }))} />
                          </div>
                          <div>
                            <label className="form-label small mb-0">Horas pagas no mês</label>
                            <input type="text" className="form-control form-control-sm" style={{ width: 120 }}
                              placeholder="00:00" value={bancoEdit.pagas}
                              onChange={(e) => setBancoEdit(b => ({ ...b, pagas: e.target.value }))} />
                          </div>
                          <div className="flex-grow-1" style={{ maxWidth: 320 }}>
                            <label className="form-label small mb-0">Observação</label>
                            <input type="text" className="form-control form-control-sm" placeholder="ex.: pago em folha 09/2026"
                              value={bancoEdit.obs} onChange={(e) => setBancoEdit(b => ({ ...b, obs: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Escape') setBancoEdit(null); }} />
                          </div>
                          <button className="btn btn-sm btn-primary" disabled={salvandoBanco}
                            onClick={() => salvarBanco(detalhe.cpf, { saldo_anterior: bancoEdit.anterior, horas_pagas: bancoEdit.pagas, obs: bancoEdit.obs })}>
                            <i className={`fas ${salvandoBanco ? 'fa-spinner fa-spin' : 'fa-save'} me-1`}></i>Salvar
                          </button>
                          <button className="btn btn-sm btn-secondary" disabled={salvandoBanco} onClick={() => setBancoEdit(null)}>Cancelar</button>
                          {(bm.anterior_origem === 'manual' || bm.pagas_seg || bm.final_seg != null) && (
                            <button className="btn btn-sm btn-outline-danger" disabled={salvandoBanco}
                              onClick={() => salvarBanco(detalhe.cpf, { remover: true })}
                              title="Apaga o ajuste deste mês (saldo anterior, horas pagas e fechamento)">
                              <i className="fas fa-trash me-1"></i>Limpar ajuste
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <table className="table table-sm align-middle mb-0">
                  <thead style={{ background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ fontSize: '.8rem' }}>Dia</th>
                      <th style={{ fontSize: '.8rem' }}>Batidas</th>
                      <th className="text-center" style={{ fontSize: '.8rem' }}
                        title="Soma das batidas do dia (entrada→saída de cada par). É o total trabalhado, antes de comparar com a jornada.">
                        Trabalhado
                      </th>
                      <th className="text-center" style={{ fontSize: '.8rem' }}>Situação</th>
                      <th className="text-center" style={{ fontSize: '.8rem' }}>Falta / Sobra</th>
                      <th className="text-center" style={{ fontSize: '.8rem' }}
                        title="Marque para não cobrar este dia do colaborador (folga combinada, compensação, dia de campo)">
                        Não cobrar
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalheDias.filter(d => d.status !== 'fora').map((d) => {
                      const st = STATUS_DIA[d.status] || STATUS_DIA.fora;
                      const sobra = d.saldoDia != null && d.saldoDia >= 0;
                      return (
                        <tr key={d.iso} style={{
                          background: d.status === 'sem_batida' ? '#fdeaec'
                            : d.status === 'ferias' ? '#e3f2fd'
                            : d.status === 'afastamento' ? '#f3e8fb' : undefined,
                          opacity: d.status === 'folga' ? .6 : 1 }}>
                          <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {d.semana}, {String(d.dia).padStart(2, '0')}/{(dadosMes?.mes || '').slice(5)}
                            {/* feriado com batida: o dia não tem jornada esperada, então tudo
                                que foi trabalhado vira sobra — sem o rótulo o "+4h" parecia erro */}
                            {d.feriado && (
                              <div className="small fw-semibold" style={{ color: '#00205b' }}
                                title={d.batidas.length
                                  ? 'Trabalhou em feriado: não há jornada a cumprir neste dia, então todas as horas contam como sobra'
                                  : 'Feriado: não há jornada a cumprir'}>
                                <i className="fas fa-star me-1"></i>feriado · {d.feriado}
                              </div>
                            )}
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: '.85rem' }}>
                            {d.batidas.length ? d.batidas.join('  ') : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums', fontSize: '.85rem' }}>
                            {d.batidas.length ? (
                              <span className="fw-semibold" title={d.batidas.length % 2
                                ? 'Batida ímpar: a última marcação não formou par e ficou fora da soma'
                                : 'Soma dos pares de batida do dia'}>
                                {fmtHoras(d.worked)}
                                {d.batidas.length % 2 === 1 && (
                                  <i className="fas fa-triangle-exclamation ms-1" style={{ color: '#b5560e', fontSize: '.7rem' }}></i>
                                )}
                              </span>
                            ) : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-center">
                            {/* intervalo < 1h: só o selo do intervalo aparece no dia */}
                            {d.intervaloCurto == null && (
                            <span className="badge" style={{
                              background: d.status === 'sem_batida' ? '#dc3545' : 'transparent',
                              color: d.status === 'sem_batida' ? '#fff' : st.cor,
                              border: d.status === 'sem_batida' ? 'none' : `1px solid ${st.cor}` }}>
                              {st.rotulo}
                            </span>
                            )}
                            {d.intervaloCurto != null && (
                              <span className="badge"
                                style={{ background: '#fff1e6', color: '#b5560e', border: '1px solid #f0a868' }}
                                title={`Não fez o intervalo mínimo de 1h: almoçou ${fmtMin(d.intervaloCurto)} (CLT art. 71)`}>
                                <i className="fas fa-utensils me-1"></i>intervalo {fmtMin(d.intervaloCurto)}
                              </span>
                            )}
                            {d.em_ausencia && (
                              <span className="badge ms-1"
                                style={{ background: '#fff1e6', color: '#b5560e', border: '1px solid #f0a868' }}
                                title="Bateu ponto neste dia mesmo estando de férias/afastado na Convenia">
                                <i className="fas fa-triangle-exclamation me-1"></i>bateu
                              </span>
                            )}
                          </td>
                          <td className="text-center fw-semibold" style={{
                            fontVariantNumeric: 'tabular-nums',
                            color: d.saldoDia == null ? '#adb5bd' : (sobra ? '#116b47' : '#a11d2b') }}>
                            {d.saldoDia == null ? '—'
                              : `${sobra ? '+' : '−'}${fmtHoras(d.saldoDia)}`}
                          </td>
                          <td className="text-center" style={{ minWidth: 190 }}>
                            {/* marcar = folga na hora, sem diálogo. O motivo é
                                um campo ao lado: preenchido, o dia passa a
                                'não cobrar' e a justificativa fica no histórico. */}
                            <div className="d-flex align-items-center gap-2">
                              <input type="checkbox" className="form-check-input mt-0"
                                checked={!!d.abono}
                                disabled={marcandoDia === d.iso || !detalhe.cpf}
                                onChange={(e) => marcarDia(detalhe.cpf, d.iso,
                                  e.target.checked ? 'folga' : null)}
                                title={!detalhe.cpf
                                  ? 'Sem CPF no cadastro — não dá para marcar'
                                  : (d.abono
                                    ? `${d.abono.tipo === 'folga' ? 'Folga' : 'Não cobrar'}${
                                        d.abono.autor ? ` — ${d.abono.autor}` : ''}`
                                    : 'Não cobrar este dia')} />
                              {d.abono && (
                                <input type="text" className="form-control form-control-sm"
                                  style={{ fontSize: '.78rem' }}
                                  defaultValue={d.abono.obs || ''}
                                  key={`ab-${d.iso}-${d.abono.obs || ''}`}
                                  placeholder="motivo (opcional)"
                                  disabled={marcandoDia === d.iso}
                                  onBlur={(e) => {
                                    const motivo = e.target.value.trim();
                                    if (motivo === (d.abono.obs || '')) return;
                                    marcarDia(detalhe.cpf, d.iso,
                                      motivo ? 'nao_cobrar' : 'folga', motivo);
                                  }}
                                  title="Com motivo o dia fica registrado como 'não cobrar'; em branco, como folga" />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer bg-light">
                <span className="me-auto small text-muted">
                  Falta/sobra só nos dias trabalhados completos; até ±10min no dia não conta (tolerância CLT);
                  férias, feriado, folga e dias sem batida ficam fora do saldo.
                  Marcar <b>Não cobrar</b> tira o dia da conta de faltas — com motivo,
                  fica registrado como “não cobrar”; sem motivo, como folga.
                </span>
                <button className="btn btn-secondary" onClick={() => setDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL: corrigir a jornada do colaborador ─── */}
      {jornadaEdit && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,.5)', zIndex: 10000 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-clock me-2 text-primary"></i>Jornada de trabalho
                  </h5>
                  <small className="text-muted">{jornadaEdit.nome}</small>
                </div>
                <button type="button" className="btn-close"
                  onClick={() => setJornadaEdit(null)}></button>
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label fw-bold small">Entrada *</label>
                    <input type="time" className="form-control" autoFocus value={jornadaEdit.entrada || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, entrada: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Escape') setJornadaEdit(null); }} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Saída *</label>
                    <input type="time" className="form-control" value={jornadaEdit.saida || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, saida: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Início do intervalo</label>
                    <input type="time" className="form-control" value={jornadaEdit.intervalo_inicio || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, intervalo_inicio: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-bold small">Fim do intervalo</label>
                    <input type="time" className="form-control" value={jornadaEdit.intervalo_fim || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, intervalo_fim: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">
                      Saída na sexta-feira <span className="badge bg-light text-muted border">opcional</span>
                    </label>
                    <input type="time" className="form-control form-control-sm" style={{ maxWidth: 160 }}
                      value={jornadaEdit.sexta_saida || ''}
                      onChange={(e) => setJornadaEdit(j => ({ ...j, sexta_saida: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold small mb-1">Dias em que não trabalha</label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((rot, idx) => {
                        const marcado = (jornadaEdit.dias_folga || []).includes(idx);
                        return (
                          <label key={rot} className={`btn btn-sm ${marcado ? 'btn-secondary' : 'btn-light border'} mb-0`}
                            title={marcado ? `${rot}: folga fixa — não é cobrado e não tem jornada esperada` : `Marcar ${rot} como dia sem trabalho`}>
                            <input type="checkbox" className="d-none" checked={marcado}
                              onChange={(e) => setJornadaEdit(j => ({
                                ...j,
                                dias_folga: e.target.checked
                                  ? [...(j.dias_folga || []), idx].sort()
                                  : (j.dias_folga || []).filter(x => x !== idx),
                              }))} />
                            {marcado && <i className="fas fa-bed me-1"></i>}{rot}
                          </label>
                        );
                      })}
                    </div>
                    <div className="form-text">Ex.: aprendiz que não vem na sexta. O dia sai da conta de faltas e não espera jornada.</div>
                  </div>
                </div>
                {/* prévia: o que os horários viram em jornada */}
                {(() => {
                  const dia = jornadaSeg(jornadaEdit);
                  const sex = jornadaEdit.sexta_saida ? jornadaSeg(jornadaEdit, 'sexta_saida') : null;
                  return (
                    <div className="form-text mt-2">
                      {dia
                        ? <>Jornada de <b>{fmtHoras(dia)}/dia</b> ({segParaMensal(dia)} por mês)
                            {sex ? <> · sexta <b>{fmtHoras(sex)}</b></> : ''}
                            {(jornadaEdit.dias_folga || []).length > 0 && <> · não trabalha: <b>{jornadaEdit.dias_folga.map(i => ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'][i]).join(', ')}</b></>}. Intervalo em branco = jornada corrida.</>
                        : 'Informe entrada e saída (a saída depois da entrada).'}
                      {jornadaEdit.manual && !jornadaEdit.entrada && ` Ajuste atual: ${jornadaEdit.mensalAtual}/mês.`}
                    </div>
                  );
                })()}

                {jornadaEdit.manual ? (
                  <div className="alert alert-warning small mt-3 mb-0">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    Esta jornada já está ajustada à mão e ignora o que vem da Convenia.
                    Use <b>Voltar para a Convenia</b> se o cadastro de lá foi corrigido.
                  </div>
                ) : (
                  <div className="alert alert-light border small mt-3 mb-0">
                    <i className="fas fa-circle-info me-2 text-primary"></i>
                    O ajuste vale só aqui no Saber e não altera a Convenia — corrija
                    lá também, senão o cadastro segue divergente.
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light">
                {jornadaEdit.manual && (
                  <button className="btn btn-outline-danger btn-sm me-auto"
                    disabled={salvandoJornada}
                    onClick={() => salvarJornada(true)}
                    title="Remove o ajuste manual e volta a usar a jornada da Convenia">
                    <i className="fas fa-rotate-left me-2"></i>Voltar para a Convenia
                  </button>
                )}
                <button className="btn btn-secondary" disabled={salvandoJornada}
                  onClick={() => setJornadaEdit(null)}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button className="btn btn-primary" disabled={salvandoJornada}
                  onClick={() => salvarJornada(false)}>
                  <i className={`fas ${salvandoJornada ? 'fa-spinner fa-spin' : 'fa-save'} me-2`}></i>
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Prévia do e-mail ao gestor: como vai chegar; o Enviar fica aqui ─── */}
      {previaEmail && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1080 }}
          onClick={(e) => { if (e.target === e.currentTarget && !enviando) setPreviaEmail(null); }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className="fas fa-envelope-open-text me-2" style={{ color: '#00b5cc' }}></i>Prévia do e-mail ao gestor
                  </h5>
                  <div className="small" style={{ color: '#6c757d' }}>
                    {previaEmail.colaboradores} colaborador(es) · confira antes de enviar
                  </div>
                </div>
                <button type="button" className="btn-close" disabled={enviando} onClick={() => setPreviaEmail(null)}></button>
              </div>
              <div className="modal-body p-0">
                <div className="px-3 py-2 border-bottom small" style={{ background: '#f8f9fa', lineHeight: 1.7 }}>
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>De</span><b>{previaEmail.de}</b></div>
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Para</span><b>{previaEmail.para}</b></div>
                  {previaEmail.cc && <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Cc</span>{previaEmail.cc}</div>}
                  {previaEmail.bcc && <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Cco</span>{previaEmail.bcc}</div>}
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Assunto</span>
                    <input type="text" className="form-control form-control-sm fw-semibold" style={{ maxWidth: 560 }}
                      value={previaEmail.assunto} maxLength={300}
                      onChange={(e) => setPreviaEmail(p => ({ ...p, assunto: e.target.value }))}
                      title="Pode editar o assunto antes de enviar" />
                  </div>
                  <div className="mt-1">
                    {!editandoPrevia ? (
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={ativarEdicaoPrevia}
                        title="Libera o texto do e-mail para edição direta na prévia">
                        <i className="fas fa-pen me-1"></i>Editar texto do e-mail
                      </button>
                    ) : (
                      <span className="badge bg-info text-dark"><i className="fas fa-pen me-1"></i>Editando — clique no texto abaixo e altere; o Enviar usa o texto editado</span>
                    )}
                  </div>
                </div>
                <iframe ref={previaRef} title="Prévia do e-mail" srcDoc={previaEmail.corpo_html}
                  style={{ width: '100%', height: '62vh', border: 0, background: '#fff' }} />
              </div>
              <div className="modal-footer bg-white">
                <button type="button" className="btn btn-secondary" disabled={enviando} onClick={() => setPreviaEmail(null)}>
                  <i className="fas fa-arrow-left me-2"></i>Voltar e ajustar
                </button>
                <button type="button" className="btn btn-success" disabled={enviando} onClick={enviar}>
                  <i className={`fas ${enviando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                  {enviando ? 'Enviando...' : `Enviar para ${previaEmail.para}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default FechamentoPonto;
